/**
 * DeadlineManager - Personal deadline system for projects
 * 
 * Place at: src/components/overlays/DeadlineManager.jsx
 * 
 * Exports:
 *   - DeadlineTab (named) — drop into ProjectDetailOverlay as a tab
 *   - DeadlineCard (named) — reusable card for showing a single deadline
 * 
 * Users set deadlines to motivate themselves to upload new work.
 * Features:
 *   - Progress ring showing time elapsed
 *   - Urgency-aware coloring (comfortable → approaching → urgent → critical)
 *   - Streak tracking for consecutive deadlines met
 *   - Recurring deadlines that auto-create the next one
 *   - Auto-completion when a new version is uploaded
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Clock, Calendar, Plus, Check, X, AlertCircle,
  RefreshCw, Flame, Trash2, ChevronDown
} from 'lucide-react';
import { cn, formatRelativeTime, formatDate } from '../../utils/cn';
import { Button, Badge, Spinner } from '../ui';
import { api } from '../../api';
import toast from 'react-hot-toast';


// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

function formatCountdown(seconds) {
  if (!seconds || seconds <= 0) return 'Expired';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function getDeadlineUrgency(dueAt) {
  if (!dueAt) return 'none';
  const hours = (new Date(dueAt) - new Date()) / 3600000;
  if (hours <= 0) return 'expired';
  if (hours <= 3) return 'critical';
  if (hours <= 24) return 'urgent';
  if (hours <= 72) return 'approaching';
  return 'comfortable';
}

function getSecondsLeft(dueAt) {
  if (!dueAt) return 0;
  return Math.max(0, (new Date(dueAt) - new Date()) / 1000);
}


// ============================================================================
// PROGRESS RING — SVG donut showing time elapsed
// ============================================================================

function ProgressRing({ percentage, size = 40, strokeWidth = 3, color = '#8b5cf6' }) {
  const r = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * r;
  const offset = circumference - (Math.min(100, Math.max(0, percentage)) / 100) * circumference;
  
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="#27272a"
        strokeWidth={strokeWidth}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        style={{ transition: 'stroke-dashoffset 0.6s ease' }}
      />
    </svg>
  );
}


// ============================================================================
// DEADLINE CARD
// ============================================================================

export function DeadlineCard({ deadline, onComplete, onDelete, compact = false }) {
  const urgency = getDeadlineUrgency(deadline.dueAt || deadline.due_at);
  const secondsLeft = (deadline.status === 'active')
    ? getSecondsLeft(deadline.dueAt || deadline.due_at)
    : 0;
  
  // Calculate progress percentage
  const progressPct = deadline.progressPercentage != null
    ? deadline.progressPercentage
    : (() => {
        const created = new Date(deadline.createdAt || deadline.created_at);
        const due = new Date(deadline.dueAt || deadline.due_at);
        const now = new Date();
        const total = due - created;
        const elapsed = now - created;
        if (total <= 0) return 100;
        return Math.min(100, Math.max(0, (elapsed / total) * 100));
      })();
  
  const urgencyStyles = {
    critical:    { ring: '#ef4444', bg: 'rgba(239,68,68,0.08)',  border: 'rgba(239,68,68,0.25)' },
    urgent:      { ring: '#f59e0b', bg: 'rgba(245,158,11,0.06)', border: 'rgba(245,158,11,0.2)' },
    approaching: { ring: '#3b82f6', bg: 'rgba(59,130,246,0.05)', border: 'rgba(59,130,246,0.15)' },
    comfortable: { ring: '#8b5cf6', bg: 'transparent',            border: 'rgba(63,63,70,0.5)' },
    expired:     { ring: '#71717a', bg: 'rgba(239,68,68,0.04)',  border: 'rgba(239,68,68,0.15)' },
    none:        { ring: '#8b5cf6', bg: 'transparent',            border: 'rgba(63,63,70,0.5)' },
  };
  const style = urgencyStyles[urgency] || urgencyStyles.comfortable;
  
  const isRecurring = deadline.isRecurring || deadline.is_recurring;
  const streakCount = deadline.streakCount || deadline.streak_count || 0;
  
  return (
    <div
      className={cn(
        'relative rounded-xl transition-all duration-200',
        deadline.status !== 'active' && 'opacity-60'
      )}
      style={{ background: style.bg, border: `1px solid ${style.border}` }}
    >
      <div className={cn('flex items-start gap-3', compact ? 'p-3' : 'p-4')}>
        {/* Progress ring or status icon */}
        <div className="relative flex-shrink-0 mt-0.5">
          {deadline.status === 'active' ? (
            <ProgressRing
              percentage={progressPct}
              size={compact ? 36 : 42}
              color={style.ring}
            />
          ) : deadline.status === 'completed' ? (
            <div className={cn(
              'rounded-full flex items-center justify-center bg-emerald-500/15',
              compact ? 'w-9 h-9' : 'w-[42px] h-[42px]'
            )}>
              <Check className="w-4 h-4 text-emerald-400" />
            </div>
          ) : deadline.status === 'missed' ? (
            <div className={cn(
              'rounded-full flex items-center justify-center bg-red-500/15',
              compact ? 'w-9 h-9' : 'w-[42px] h-[42px]'
            )}>
              <AlertCircle className="w-4 h-4 text-red-400" />
            </div>
          ) : (
            <div className={cn(
              'rounded-full flex items-center justify-center bg-zinc-800',
              compact ? 'w-9 h-9' : 'w-[42px] h-[42px]'
            )}>
              <X className="w-4 h-4 text-zinc-500" />
            </div>
          )}
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className={cn(
              'font-medium text-zinc-100 truncate',
              compact ? 'text-sm' : 'text-[15px]'
            )}>
              {deadline.title}
            </h4>
            {isRecurring && (
              <span className="flex items-center gap-0.5 text-[10px] text-purple-400 bg-purple-500/10 px-1.5 py-0.5 rounded-full border border-purple-500/20 flex-shrink-0">
                <RefreshCw className="w-2.5 h-2.5" />
                recurring
              </span>
            )}
          </div>
          
          {!compact && (deadline.description) && (
            <p className="text-xs text-zinc-500 mt-1 line-clamp-1">{deadline.description}</p>
          )}
          
          <div className="flex items-center gap-3 mt-1.5 flex-wrap">
            {deadline.status === 'active' ? (
              <>
                <span className={cn(
                  'text-xs font-medium',
                  urgency === 'critical' ? 'text-red-400' :
                  urgency === 'urgent' ? 'text-amber-400' : 'text-zinc-400'
                )}>
                  {formatCountdown(secondsLeft)} left
                </span>
                <span className="text-zinc-700">·</span>
                <span className="text-xs text-zinc-500">
                  Due {formatDate(deadline.dueAt || deadline.due_at)}
                </span>
              </>
            ) : deadline.status === 'completed' ? (
              <span className="text-xs text-emerald-400">
                Completed {deadline.completedAt || deadline.completed_at
                  ? formatRelativeTime(deadline.completedAt || deadline.completed_at)
                  : ''}
              </span>
            ) : deadline.status === 'missed' ? (
              <span className="text-xs text-red-400">Missed</span>
            ) : (
              <span className="text-xs text-zinc-500">Cancelled</span>
            )}
            
            {streakCount > 0 && (
              <>
                <span className="text-zinc-700">·</span>
                <span className="flex items-center gap-1 text-xs text-amber-400">
                  <Flame className="w-3 h-3" />
                  {streakCount} streak
                </span>
              </>
            )}
          </div>
        </div>
        
        {/* Actions for active deadlines */}
        {deadline.status === 'active' && (
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => onComplete?.(deadline)}
              className="p-1.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 transition-colors"
              title="Mark completed"
            >
              <Check className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => onDelete?.(deadline)}
              className="p-1.5 rounded-lg hover:bg-zinc-800 text-zinc-500 hover:text-zinc-300 transition-colors"
              title="Cancel deadline"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


// ============================================================================
// DEADLINE CREATOR — Inline form for setting new deadlines
// ============================================================================

function DeadlineCreator({ projectId, onSuccess, onCancel }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [dueTime, setDueTime] = useState('23:59');
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrenceDays, setRecurrenceDays] = useState(7);
  const [submitting, setSubmitting] = useState(false);
  const titleRef = useRef(null);
  
  useEffect(() => {
    const timer = setTimeout(() => titleRef.current?.focus(), 100);
    return () => clearTimeout(timer);
  }, []);
  
  const handleSubmit = async () => {
    const trimmed = title.trim();
    if (!trimmed || !dueDate) return;
    
    setSubmitting(true);
    try {
      const payload = {
        title: trimmed,
        description: description.trim() || undefined,
        due_at: `${dueDate}T${dueTime}:00`,
        is_recurring: isRecurring,
        recurrence_days: isRecurring ? recurrenceDays : undefined,
      };
      
      const response = await api.post(`/projects/${projectId}/deadlines`, payload);
      toast.success('Deadline set!');
      onSuccess?.(response.data.deadline || response.data);
    } catch (err) {
      console.error('Failed to create deadline:', err);
      toast.error(
        err.response?.data?.error ||
        err.response?.data?.msg ||
        'Failed to create deadline'
      );
    } finally {
      setSubmitting(false);
    }
  };
  
  // Minimum date is today
  const today = new Date().toISOString().split('T')[0];
  
  return (
    <div className="rounded-xl border border-purple-500/20 bg-purple-500/5 overflow-hidden">
      <div className="h-[2px] bg-gradient-to-r from-purple-500 via-indigo-500 to-purple-600" />
      <div className="p-4 space-y-3">
        {/* Title */}
        <input
          ref={titleRef}
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="What do you want to finish?"
          className="w-full bg-transparent text-zinc-100 text-[15px] font-medium placeholder:text-zinc-600 focus:outline-none"
          onKeyDown={(e) => { if (e.key === 'Enter' && title.trim() && dueDate) handleSubmit(); }}
        />
        
        {/* Description */}
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional details..."
          className="w-full bg-zinc-900/40 border border-zinc-700/30 rounded-lg px-3 py-2 text-sm text-zinc-300 placeholder:text-zinc-600 focus:outline-none focus:border-purple-500/30 focus:ring-1 focus:ring-purple-500/20 resize-none transition-colors"
          rows={2}
        />
        
        {/* Date and time */}
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <label className="block text-xs text-zinc-500 mb-1">Due Date</label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              min={today}
              className="w-full bg-zinc-900/40 border border-zinc-700/30 rounded-lg px-3 py-2 text-sm text-zinc-200 focus:outline-none focus:border-purple-500/30 focus:ring-1 focus:ring-purple-500/20 transition-colors [color-scheme:dark]"
            />
          </div>
          <div className="w-28">
            <label className="block text-xs text-zinc-500 mb-1">Time</label>
            <input
              type="time"
              value={dueTime}
              onChange={(e) => setDueTime(e.target.value)}
              className="w-full bg-zinc-900/40 border border-zinc-700/30 rounded-lg px-3 py-2 text-sm text-zinc-200 focus:outline-none focus:border-purple-500/30 focus:ring-1 focus:ring-purple-500/20 transition-colors [color-scheme:dark]"
            />
          </div>
        </div>
        
        {/* Recurring toggle */}
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 cursor-pointer group">
            <button
              type="button"
              role="switch"
              aria-checked={isRecurring}
              onClick={() => setIsRecurring(!isRecurring)}
              className={cn(
                'relative w-8 h-[18px] rounded-full transition-colors duration-200',
                isRecurring ? 'bg-purple-500' : 'bg-zinc-700'
              )}
            >
              <div className={cn(
                'absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white shadow transition-all duration-200',
                isRecurring ? 'left-[16px]' : 'left-[2px]'
              )} />
            </button>
            <span className="text-xs text-zinc-400 group-hover:text-zinc-300 select-none">
              Recurring
            </span>
          </label>
          
          {isRecurring && (
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-zinc-500">every</span>
              <input
                type="number"
                min={1}
                max={365}
                value={recurrenceDays}
                onChange={(e) => setRecurrenceDays(parseInt(e.target.value) || 7)}
                className="w-14 bg-zinc-900/40 border border-zinc-700/30 rounded-lg px-2 py-1 text-sm text-zinc-200 text-center focus:outline-none focus:border-purple-500/30 transition-colors"
              />
              <span className="text-xs text-zinc-500">days</span>
            </div>
          )}
        </div>
        
        {/* Actions */}
        <div className="flex items-center justify-end gap-2 pt-1">
          <Button variant="ghost" size="sm" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={!title.trim() || !dueDate || submitting}
            loading={submitting}
          >
            Set Deadline
          </Button>
        </div>
      </div>
    </div>
  );
}


// ============================================================================
// MAIN EXPORT: DeadlineTab
// ============================================================================

export function DeadlineTab({ projectId, isOwner = false }) {
  const [deadlines, setDeadlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreator, setShowCreator] = useState(false);
  
  // Fetch deadlines
  useEffect(() => {
    if (!projectId) return;
    
    let cancelled = false;
    
    const fetchDeadlines = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/projects/${projectId}/deadlines`, {
          params: { status: 'all' }
        });
        if (!cancelled) {
          setDeadlines(response.data.deadlines || []);
        }
      } catch (err) {
        console.error('Failed to fetch deadlines:', err);
        if (!cancelled) {
          setDeadlines([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    
    fetchDeadlines();
    return () => { cancelled = true; };
  }, [projectId]);
  
  // Complete a deadline
  const handleComplete = async (deadline) => {
    try {
      await api.post(`/projects/${projectId}/deadlines/${deadline.id}/complete`);
      setDeadlines(prev => prev.map(d =>
        d.id === deadline.id
          ? { ...d, status: 'completed', completedAt: new Date().toISOString(), streakCount: (d.streakCount || 0) + 1 }
          : d
      ));
      toast.success('Deadline completed!');
    } catch (err) {
      console.error('Failed to complete deadline:', err);
      toast.error('Failed to complete deadline');
    }
  };
  
  // Cancel/delete a deadline
  const handleDelete = async (deadline) => {
    try {
      await api.delete(`/projects/${projectId}/deadlines/${deadline.id}`);
      setDeadlines(prev => prev.map(d =>
        d.id === deadline.id ? { ...d, status: 'cancelled' } : d
      ));
      toast.success('Deadline cancelled');
    } catch (err) {
      console.error('Failed to cancel deadline:', err);
      toast.error('Failed to cancel deadline');
    }
  };
  
  // After creating a new deadline
  const handleCreated = (newDeadline) => {
    setDeadlines(prev => [newDeadline, ...prev]);
    setShowCreator(false);
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="md" />
      </div>
    );
  }
  
  const activeDeadlines = deadlines.filter(d => d.status === 'active');
  const pastDeadlines = deadlines.filter(d => d.status !== 'active');
  
  return (
    <div className="space-y-3">
      {/* Create button */}
      {isOwner && !showCreator && (
        <button
          onClick={() => setShowCreator(true)}
          className="w-full flex items-center justify-center gap-2 p-3 rounded-xl border-2 border-dashed border-zinc-700/60 hover:border-purple-500/40 hover:bg-purple-500/5 text-zinc-400 hover:text-purple-300 transition-all text-sm font-medium"
        >
          <Plus className="w-4 h-4" />
          Set a New Deadline
        </button>
      )}
      
      {/* Creator form */}
      {showCreator && (
        <DeadlineCreator
          projectId={projectId}
          onSuccess={handleCreated}
          onCancel={() => setShowCreator(false)}
        />
      )}
      
      {/* Active deadlines */}
      {activeDeadlines.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">
            Active
          </h3>
          <div className="space-y-2">
            {activeDeadlines.map(d => (
              <DeadlineCard
                key={d.id}
                deadline={d}
                onComplete={handleComplete}
                onDelete={handleDelete}
              />
            ))}
          </div>
        </div>
      )}
      
      {/* Past deadlines */}
      {pastDeadlines.length > 0 && (
        <div className={activeDeadlines.length > 0 ? 'mt-4' : ''}>
          <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">
            History
          </h3>
          <div className="space-y-1.5">
            {pastDeadlines.map(d => (
              <DeadlineCard key={d.id} deadline={d} compact />
            ))}
          </div>
        </div>
      )}
      
      {/* Empty state */}
      {deadlines.length === 0 && !showCreator && (
        <div className="text-center py-12">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-zinc-800/50 flex items-center justify-center mb-4">
            <Calendar className="w-6 h-6 text-zinc-600" />
          </div>
          <p className="text-zinc-400 text-sm font-medium mb-1">No deadlines set</p>
          <p className="text-zinc-600 text-xs">
            {isOwner
              ? 'Set personal deadlines to stay motivated and build creative momentum'
              : 'The creator hasn\'t set any deadlines for this project'}
          </p>
        </div>
      )}
    </div>
  );
}

export default DeadlineTab;